// Render-time quarantine + size-cap markers.
//
// The quarantine rules themselves now live in @hc/normalised-metrics
// (Phase 4) — the daily ingest pipeline writes is_quarantined and
// quarantine_reasons directly to entity_normalised_metrics. UI code
// should prefer row.isQuarantined when available. This module:
//   1. Re-exports the shared rule helpers so existing imports keep
//      working without churn.
//   2. Adds the screener/company-overview view adapters that pull the
//      right fields off the API row shapes.
//   3. Holds the size-class-cap markers (display-only, never written
//      to the DB).

import type { ScreenerRow } from '../api/screener-client.js';
import type { CompanyMetrics } from '../api/screener-client.js';
import { NATIVE_CURRENCY_BY_COUNTRY, convertCurrency } from '../currency.js';
import {
  evaluateQuarantine,
  describeQuarantine as describeQuarantineShared,
  type QuarantineInputs,
  type QuarantineReason as SharedQuarantineReason,
  type QuarantineResult,
} from '@hc/normalised-metrics';

export type QuarantineReason = SharedQuarantineReason;
export type RowQuality = QuarantineResult;

/** Evaluate one row against the quarantine heuristics — alias for the
 *  shared package's evaluateQuarantine, kept for backwards-compat with
 *  existing imports. */
export function evaluateRowQuality(r: QuarantineInputs): RowQuality {
  return evaluateQuarantine(r);
}

/** The scale-leak ceilings are denominated in EUR, but a row carries its
 *  turnover in NATIVE currency. Rebase before comparing, or a weak-currency
 *  market reads as an order-of-magnitude leak purely because of its unit
 *  (7.9bn HUF = €19.9M, not a €7.9bn hospital). Returns null when the
 *  currency is unknown — evaluateQuarantine then has no EUR value and the
 *  ceiling check correctly stays silent rather than guessing. */
function turnoverEurOf(turnover: number | null, countryCode: string | null): number | null {
  if (turnover === null) return null;
  const native = countryCode ? NATIVE_CURRENCY_BY_COUNTRY[countryCode] : undefined;
  return native ? convertCurrency(turnover, native, 'EUR') : null;
}

export function evaluateScreenerRowQuality(row: ScreenerRow): RowQuality {
  return evaluateQuarantine({
    turnoverScreener: row.turnoverScreener,
    turnoverBasis: row.turnoverBasis,
    employees: row.employees,
    turnoverEur: turnoverEurOf(row.turnoverScreener, row.countryCode),
    subsegment: row.subsegment,
  });
}

export function evaluateMetricsQuality(m: CompanyMetrics | null): RowQuality {
  if (!m) return { quarantined: false, reasons: [] };
  return evaluateQuarantine({
    turnoverScreener: m.turnoverScreener,
    turnoverBasis: m.turnoverBasis,
    employees: m.employees,
    subsegment: m.subsegment,
  });
}

export function describeQuarantine(reasons: QuarantineReason[]): string {
  return describeQuarantineShared(reasons);
}

/** Size-class cap markers — when a modelled value sits exactly on one
 *  of these thresholds, the model was capped (it didn't actually
 *  estimate this value, it ran into the ceiling). Display a (cap)
 *  marker. Render-only — never written to the DB. */
const SIZE_CLASS_CAPS = new Set([
  632_000, 10_200_000, 36_000_000, 1_000_000, 15_000_000, 54_000_000,
]);

export function isAtSizeClassCap(
  turnoverScreener: number | null,
  turnoverBasis: string | null,
): boolean {
  if (turnoverScreener === null) return false;
  const basis = (turnoverBasis ?? '').toLowerCase();
  if (basis.includes('reported') || basis.includes('dormant')) return false;
  return SIZE_CLASS_CAPS.has(turnoverScreener);
}
